/**
 * Herramienta de selección: clic, marco (rubber band), lazo, mover, escalar y rotar.
 * También abre la edición de texto con doble clic.
 */
import type { Shape } from '../../shared/types.ts'
import { rotatePoint, snapTo, type Box } from '../lib/geom.ts'
import { GRID_SIZE } from '../canvas/background.ts'
import { COLLAPSE_BADGE_R, collapseBadgeCenter } from '../canvas/render-shape.ts'
import { addFloating, reparent, settleDrop, toggleCollapse } from '../mindmap/actions.ts'
import { hitTest, shapesInBox, shapesInLasso } from '../canvas/hit.ts'
import { shapeBounds } from '../state/shapes.ts'
import type { AppContext, PointerInfo, Tool } from './types.ts'

type Handle = 'nw' | 'ne' | 'se' | 'sw' | 'rotate' | null

type Mode = 'idle' | 'marquee' | 'lasso' | 'translate' | 'resize' | 'rotate' | 'pending'

const HANDLE_HIT = 12

export class SelectTool implements Tool {
  private lastBadgeAt = Number.NEGATIVE_INFINITY
  readonly id = 'select' as const
  private ctx: AppContext
  private mode: Mode = 'idle'
  private start = { x: 0, y: 0 }
  private current = { x: 0, y: 0 }
  private lasso: number[] = []
  private initial: Shape[] = []
  private initialBounds: Box | null = null
  private handle: Handle = null
  private removePainter: (() => void) | null = null
  private additive = false
  private startRotation = 0
  private movedEnough = false
  readonly lassoMode: boolean

  constructor(ctx: AppContext, lassoMode = false) {
    this.ctx = ctx
    this.lassoMode = lassoMode
  }

  get cursor(): string {
    return this.lassoMode ? 'crosshair' : 'default'
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c, cam) => this.paint(c, cam))
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    this.mode = 'idle'
  }

  /** Detecta si el puntero está sobre una manija de la selección. */
  /** ¿El punto cae en la insignia de plegado de algún tema? */
  private collapseBadgeAt(x: number, y: number): string | null {
    const tolerance = 4 / this.ctx.store.camera.z
    for (const shape of this.ctx.shapes()) {
      if (shape.type !== 'topic' || !shape.hiddenCount || shape.hidden) continue
      const at = collapseBadgeCenter(shape)
      if (Math.hypot(x - at.x, y - at.y) <= COLLAPSE_BADGE_R + tolerance) return shape.id
    }
    return null
  }

  /** Los temas no se escalan ni rotan a mano: su tamaño lo pone el layout. */
  private selectionHasTopic(): boolean {
    const { store, board } = this.ctx
    return store.selection.some((id) => {
      const s = board.get(id)
      return s?.type === 'topic' || s?.type === 'relation'
    })
  }

  private handleAt(sx: number, sy: number): Handle {
    const bounds = this.ctx.renderer.selectionBounds()
    if (!bounds || this.ctx.store.editingId) return null
    if (this.selectionHasTopic()) return null
    const r = this.ctx.renderer
    const corners: [Handle, { x: number; y: number }][] = [
      ['nw', r.worldToScreen(bounds.x, bounds.y)],
      ['ne', r.worldToScreen(bounds.x + bounds.w, bounds.y)],
      ['se', r.worldToScreen(bounds.x + bounds.w, bounds.y + bounds.h)],
      ['sw', r.worldToScreen(bounds.x, bounds.y + bounds.h)],
    ]
    for (const [name, pt] of corners) {
      if (Math.abs(sx - pt.x) <= HANDLE_HIT && Math.abs(sy - pt.y) <= HANDLE_HIT) return name
    }
    const top = r.worldToScreen(bounds.x + bounds.w / 2, bounds.y)
    if (Math.abs(sx - top.x) <= HANDLE_HIT && Math.abs(sy - (top.y - 26)) <= HANDLE_HIT) return 'rotate'
    return null
  }

  cursorFor(sx: number, sy: number): string {
    const handle = this.handleAt(sx, sy)
    if (handle === 'rotate') return 'grab'
    if (handle === 'nw' || handle === 'se') return 'nwse-resize'
    if (handle === 'ne' || handle === 'sw') return 'nesw-resize'
    return this.lassoMode ? 'crosshair' : 'default'
  }

  onPointerDown(p: PointerInfo): void {
    const { store, board } = this.ctx
    // Insignia de rama plegada: un toque la abre o la cierra.
    const badge = this.ctx.store.readOnly ? null : this.collapseBadgeAt(p.x, p.y)
    if (badge) {
      this.lastBadgeAt = performance.now()
      toggleCollapse(board, badge)
      this.mode = 'idle'
      return
    }
    this.start = { x: p.x, y: p.y }
    this.current = { x: p.x, y: p.y }
    this.additive = p.shiftKey || p.metaKey || p.ctrlKey
    this.movedEnough = false
    board.undoManager.stopCapturing()

    const handle = this.handleAt(p.sx, p.sy)
    if (handle) {
      this.handle = handle
      this.initial = store.selection.map((id) => board.get(id)!).filter(Boolean)
      this.initialBounds = this.ctx.renderer.selectionBounds()
      this.mode = handle === 'rotate' ? 'rotate' : 'resize'
      this.startRotation = this.initial[0]?.rotation ?? 0
      return
    }

    const hit = hitTest(this.ctx.shapes(), p.x, p.y, store.camera.z)
    if (hit) {
      // Modo "conectar" del mapa: este clic elige el otro extremo de la relación.
      if (hit.type === 'topic' && this.ctx.maybeCompleteRelation?.(hit.id)) {
        this.mode = 'idle'
        return
      }
      if (this.additive) {
        const next = store.selection.includes(hit.id)
          ? store.selection.filter((id) => id !== hit.id)
          : [...store.selection, hit.id]
        store.setSelection(next)
      } else if (!store.selection.includes(hit.id)) {
        store.setSelection([hit.id])
      }
      this.initial = store.selection.map((id) => board.get(id)!).filter(Boolean)
      this.mode = 'pending'
      return
    }

    if (this.ctx.hasPendingRelation) this.ctx.cancelPendingRelation?.()
    if (!this.additive) store.setSelection([])
    store.setEditing(null)
    // Cinturón y tirantes: con el rechazo de palma encendido, un dedo jamás
    // abre marco ni lazo, llegue por la ruta que llegue. La mano apoyada era
    // exactamente esto: un "dedo" que barría el lienzo y lo seleccionaba todo.
    if (p.pointerType === 'touch' && store.palmRejection) {
      this.mode = 'idle'
      return
    }
    this.mode = this.lassoMode ? 'lasso' : 'marquee'
    this.lasso = [p.x, p.y]
  }

  onPointerMove(p: PointerInfo): void {
    this.current = { x: p.x, y: p.y }
    const dx = p.x - this.start.x
    const dy = p.y - this.start.y
    if (!this.movedEnough && Math.hypot(dx, dy) * this.ctx.store.camera.z > 3) this.movedEnough = true

    switch (this.mode) {
      case 'pending':
        if (this.movedEnough) this.mode = 'translate'
        break
      case 'marquee':
      case 'lasso':
        if (this.mode === 'lasso') this.lasso.push(p.x, p.y)
        this.ctx.renderer.invalidateOverlay()
        return
      default:
        break
    }

    if (this.mode === 'translate') this.applyTranslate(dx, dy, p)
    else if (this.mode === 'resize') this.applyResize(p)
    else if (this.mode === 'rotate') this.applyRotate(p)
  }

  onPointerUp(): void {
    const { store, board } = this.ctx
    if (this.mode === 'marquee') {
      const box = normalizeBox(this.start, this.current)
      if (box.w > 2 || box.h > 2) {
        const found = shapesInBox(this.ctx.shapes(), box).map((s) => s.id)
        store.setSelection(this.additive ? unique([...store.selection, ...found]) : found)
      }
    } else if (this.mode === 'lasso') {
      if (this.lasso.length > 6) {
        const found = shapesInLasso(this.ctx.shapes(), this.lasso).map((s) => s.id)
        store.setSelection(this.additive ? unique([...store.selection, ...found]) : found)
      }
      this.lasso = []
    } else if (this.mode === 'translate' || this.mode === 'resize' || this.mode === 'rotate') {
      this.finishTopicDrag()
      this.ctx.touch()
      board.undoManager.stopCapturing()
    }
    this.mode = 'idle'
    this.handle = null
    this.initial = []
    this.ctx.renderer.invalidateOverlay()
  }

  onDoubleClick(p: PointerInfo): void {
    // El segundo toque de un doble toque en la insignia de plegado no es un
    // doble clic en el vacío: sin esto se creaba un tema flotante basura.
    if (performance.now() - this.lastBadgeAt < 600) return
    const hit = hitTest(this.ctx.shapes(), p.x, p.y, this.ctx.store.camera.z)
    if (!hit) {
      // En un mapa, doble clic en el vacío crea un tema flotante (como XMind).
      if (this.ctx.isMindMap && !this.ctx.store.readOnly) {
        const topic = addFloating(this.ctx.board, this.ctx.getUserName(), p.x, p.y)
        this.ctx.store.setSelection([topic.id])
        this.ctx.editText(topic.id, true)
      }
      return
    }
    if (hit.type === 'note' || hit.type === 'text' || hit.type === 'shape' || hit.type === 'topic') {
      this.ctx.store.setSelection([hit.id])
      this.ctx.editText(hit.id, true)
    }
  }

  /**
   * Fin del arrastre de un tema: si cayó sobre otro tema se cuelga de él (con
   * el orden según dónde cayó); si cayó en el vacío, una raíz se queda anclada
   * ahí y un hijo vuelve a su sitio. El layout asienta todo en ambos casos.
   */
  private finishTopicDrag(): void {
    const { store, board } = this.ctx
    if (store.readOnly) return
    if (this.mode !== 'translate' || store.selection.length !== 1) return
    const dragged = board.get(store.selection[0])
    if (dragged?.type !== 'topic') return
    // El punto de suelta sale del PUNTERO (foto inicial + desplazamiento), no
    // del tablero: la reconciliación del mapa pudo devolver el tema a su hueco
    // mientras el usuario apuntaba quieto sobre el destino.
    const foto = this.initial.find((s) => s.id === dragged.id) ?? dragged
    const cx = foto.x + foto.w / 2 + (this.current.x - this.start.x)
    const cy = foto.y + foto.h / 2 + (this.current.y - this.start.y)
    const target = this.ctx
      .shapes()
      .filter(
        (s): s is typeof dragged =>
          s.type === 'topic' && s.id !== dragged.id && !s.hidden,
      )
      .filter((s) => cx > s.x - 20 && cx < s.x + s.w + 20 && cy > s.y - 14 && cy < s.y + s.h + 14)
      // Con temas encimados gana el más cercano al punto de suelta.
      .sort(
        (a, b) =>
          Math.hypot(cx - (a.x + a.w / 2), cy - (a.y + a.h / 2)) -
          Math.hypot(cx - (b.x + b.w / 2), cy - (b.y + b.h / 2)),
      )[0]

    if (target) {
      const ok = reparent(board, dragged.id, target.id, cx, cy)
      if (!ok) {
        this.ctx.toast('No se puede colgar una rama de su propio subtema')
        settleDrop(board, dragged.id)
      }
      return
    }
    settleDrop(board, dragged.id)
  }

  onCancel(): void {
    if (this.initial.length > 0 && (this.mode === 'translate' || this.mode === 'resize' || this.mode === 'rotate')) {
      // Se devuelve la geometría inicial SOLO a lo que sigue existiendo, y sobre
      // su estado actual: ni resucitar lo borrado ni pisar textos ajenos.
      const now = Date.now()
      this.ctx.board.putMany(
        this.initial.flatMap((snapshot) => {
          const current = this.ctx.board.get(snapshot.id)
          if (!current) return []
          return [
            {
              ...current,
              x: snapshot.x,
              y: snapshot.y,
              w: snapshot.w,
              h: snapshot.h,
              rotation: snapshot.rotation,
              updatedAt: now,
            } as typeof current,
          ]
        }),
      )
    }
    this.mode = 'idle'
    this.lasso = []
    this.ctx.renderer.invalidateOverlay()
  }

  private applyTranslate(dx: number, dy: number, p: PointerInfo): void {
    let ddx = dx
    let ddy = dy
    if (p.shiftKey) {
      // Restringe el movimiento al eje dominante.
      if (Math.abs(dx) > Math.abs(dy)) ddy = 0
      else ddx = 0
    }
    if (this.ctx.store.snapToGrid && this.initial[0]) {
      ddx = snapTo(this.initial[0].x + ddx, GRID_SIZE) - this.initial[0].x
      ddy = snapTo(this.initial[0].y + ddy, GRID_SIZE) - this.initial[0].y
    }
    const now = Date.now()
    // Se parte de la figura tal como está AHORA en el tablero y solo se le cambia
    // la geometría. Si otro le cambió el texto o el color mientras arrastrábamos,
    // su cambio sobrevive; antes lo borraba la foto tomada al empezar el arrastre.
    this.ctx.board.putMany(
      this.initial
        // La posición de una relación es derivada de sus extremos: no se arrastra.
        .filter((snapshot) => snapshot.type !== 'relation')
        // Si otro la borró durante el gesto, no se resucita desde la foto.
        .filter((snapshot) => this.ctx.board.get(snapshot.id) !== undefined)
        .map((snapshot) => {
          const current = this.ctx.board.get(snapshot.id)!
          return { ...current, x: snapshot.x + ddx, y: snapshot.y + ddy, updatedAt: now }
        }),
    )
  }

  private applyResize(p: PointerInfo): void {
    const box = this.initialBounds
    if (!box || this.initial.length === 0) return
    // La esquina opuesta a la manija queda fija; la arrastrada sigue al puntero.
    const grabLeft = this.handle === 'nw' || this.handle === 'sw'
    const grabTop = this.handle === 'nw' || this.handle === 'ne'
    const anchor = {
      x: grabLeft ? box.x + box.w : box.x,
      y: grabTop ? box.y + box.h : box.y,
    }
    const oldDX = (grabLeft ? box.x : box.x + box.w) - anchor.x
    const oldDY = (grabTop ? box.y : box.y + box.h) - anchor.y
    let sx = oldDX === 0 ? 1 : (p.x - anchor.x) / oldDX
    let sy = oldDY === 0 ? 1 : (p.y - anchor.y) / oldDY

    if (!Number.isFinite(sx) || sx === 0) sx = 0.01
    if (!Number.isFinite(sy) || sy === 0) sy = 0.01

    // Proporcional por defecto para imágenes y notas; libre con Alt.
    const keepAspect = !p.altKey && this.initial.some((s) => s.type === 'image' || s.type === 'note')
    if (keepAspect || p.shiftKey) {
      const s = Math.max(Math.abs(sx), Math.abs(sy))
      sx = Math.sign(sx) * s
      sy = Math.sign(sy) * s
    }
    sx = clampScale(sx)
    sy = clampScale(sy)

    const now = Date.now()
    this.ctx.board.putMany(
      this.initial.filter((snapshot) => this.ctx.board.get(snapshot.id) !== undefined).map((snapshot) => {
        // Igual que al mover: la geometría sale de la foto, lo demás del tablero vivo.
        const current = this.ctx.board.get(snapshot.id)!
        const nx = anchor.x + (snapshot.x - anchor.x) * sx
        const ny = anchor.y + (snapshot.y - anchor.y) * sy
        const nw = snapshot.w * sx
        const nh = snapshot.h * sy
        const next: Shape = {
          ...current,
          x: nw < 0 ? nx + nw : nx,
          y: nh < 0 ? ny + nh : ny,
          w: Math.max(Math.abs(nw), 4),
          h: Math.max(Math.abs(nh), 4),
          updatedAt: now,
        }
        if (next.type === 'text' && snapshot.type === 'text') {
          // El texto conserva la altura calculada por su contenido.
          next.fontSize = Math.max(8, snapshot.fontSize * Math.abs(sx))
        }
        return next
      }),
    )
  }

  private applyRotate(p: PointerInfo): void {
    const box = this.initialBounds
    if (!box || this.initial.length === 0) return
    const cx = box.x + box.w / 2
    const cy = box.y + box.h / 2
    const startAngle = Math.atan2(this.start.y - cy, this.start.x - cx)
    const angle = Math.atan2(p.y - cy, p.x - cx)
    let delta = angle - startAngle
    if (p.shiftKey) delta = Math.round(delta / (Math.PI / 12)) * (Math.PI / 12)
    const now = Date.now()
    this.ctx.board.putMany(
      this.initial.filter((snapshot) => this.ctx.board.get(snapshot.id) !== undefined).map((snapshot) => {
        const current = this.ctx.board.get(snapshot.id)!
        const shapeCx = snapshot.x + snapshot.w / 2
        const shapeCy = snapshot.y + snapshot.h / 2
        const rotated = rotatePoint(shapeCx, shapeCy, cx, cy, delta)
        return {
          ...current,
          x: rotated.x - snapshot.w / 2,
          y: rotated.y - snapshot.h / 2,
          rotation: (snapshot.rotation ?? 0) + delta,
          updatedAt: now,
        }
      }),
    )
    void this.startRotation
  }

  private paint(c: CanvasRenderingContext2D, camera: { z: number }): void {
    const inv = 1 / camera.z
    if (this.mode === 'marquee') {
      const box = normalizeBox(this.start, this.current)
      c.save()
      c.fillStyle = 'rgba(15,108,189,0.08)'
      c.strokeStyle = '#0F6CBD'
      c.lineWidth = 1.5 * inv
      c.fillRect(box.x, box.y, box.w, box.h)
      c.strokeRect(box.x, box.y, box.w, box.h)
      c.restore()
    } else if (this.mode === 'lasso' && this.lasso.length >= 4) {
      c.save()
      c.beginPath()
      c.moveTo(this.lasso[0], this.lasso[1])
      for (let i = 2; i < this.lasso.length; i += 2) c.lineTo(this.lasso[i], this.lasso[i + 1])
      c.closePath()
      c.fillStyle = 'rgba(15,108,189,0.08)'
      c.strokeStyle = '#0F6CBD'
      c.lineWidth = 1.5 * inv
      c.setLineDash([6 * inv, 4 * inv])
      c.fill()
      c.stroke()
      c.restore()
    }
  }
}

function clampScale(s: number): number {
  const abs = Math.min(Math.max(Math.abs(s), 0.02), 40)
  return Math.sign(s) * abs
}

function normalizeBox(a: { x: number; y: number }, b: { x: number; y: number }): Box {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  }
}

function unique(ids: string[]): string[] {
  return Array.from(new Set(ids))
}

export { shapeBounds }
